import {Button} from "@sgi/gravity/Button";
import {Input} from "@sgi/gravity/Input";
import {Typography} from "@sgi/gravity/Typography";
import {PasswordInput} from "@sgi/gravity/PasswordInput";
import {ErrorAlert} from "@sgi/gravity/Alert";
import {Box, Flex, Spacer, Stack, Center, useBreakpoint, StyleProps} from "@chakra-ui/react";
import {ChangeEvent, FC, useCallback, useState, useEffect, KeyboardEvent, ReactNode} from "react";
import "./signIn.css";
import {useWrapper} from "../Context/WrapperContext";
import ButtonLink from "../Components/ButtonLink";
import { useInterceptor } from "../Custom Hooks/useInterceptor";
import { useUnifiedLayoutProxy } from "../Custom Hooks/useUnifiedLayoutProxy";
import useFocusControl from "../Custom Hooks/useFocusControl";
import Title from "../Common/Title";
import CancelButton from "../Common/Form/CancelButton";
import { useConfig } from "../Custom Hooks/useConfig";

export default function SignIn() {
  const {increment} = useWrapper();
  const config = useConfig();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const {
    signInAction, navigateToForgotPasswordAction,
    signInError, emailError, passwordError,
  } = useUnifiedLayoutProxy({email, password});

  const ref = useFocusControl(index => index === 0);

  const [errorMessage, setErrorMessage] = useState<ReactNode>('');
  const mapErrorMessage = (apiErrorMessage: string): ReactNode => {
    if (
      apiErrorMessage.includes('Your password is incorrect.') ||
      apiErrorMessage.includes('The supplied credential is invalid.')
    ) {
      return (
        <>
          {'Invalid username or password. Please check your credentials and try again. If you don\'t have an account, please click the "'}
          <span style={{ textDecoration: 'underline', color: '#424242'}}>
             <b>{'Register here'}</b>
          </span>
          {'" link below.'}
        </>
      );
    }

    if (apiErrorMessage.includes("Your account has been temporarily locked out.")) {
      return "As a security precaution, the user account has been locked because there were too many login attempts. Wait a while before trying again.";
    }

    if (apiErrorMessage.includes('Your account is already migrated.')) {
      return (
        <>
          {'It looks like you\'re using an old email or username to sign in. Please check your email and try again. Need help? Click the "'}
          <span style={{ textDecoration: 'underline', color: '#424242'}}>
            <b>{'Forgot Username or Email'}</b>
          </span>
          {'" link below.'}
        </>
      );
    }

    if (apiErrorMessage.startsWith('No code provided: ')) {
      return apiErrorMessage.slice(18, apiErrorMessage.lastIndexOf("; RequestId: "));
    }

    return apiErrorMessage;
  };

  const interceptedError = useInterceptor();

  useEffect(() => {
    setErrorMessage(mapErrorMessage(interceptedError));
  }, [interceptedError]);

  useEffect(() => {
    setErrorMessage(mapErrorMessage(signInError));
  }, [signInError]);

  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value), []);
  const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value), []);
  const navigateToCreateAccount = (): void => {
    window.location.assign(config.SGI_REGISTRATION_URL);
  };

  const onVerifyKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") signInAction.act();
    },
    [signInAction]
  );

  return (
    <Flex ref={ref}
          direction="column"
          maxW={544} padding={38} mx="auto"
          data-testid="signin-test"
    >
      <Title text="Sign in to MySGI"/>
      <Typography variant="body" mb={8}>
        We've made some changes to MySGI. We'll walk you through a few simple
        steps to update your account.
      </Typography>
      <Box data-testid="error-container-test" mb={8}>
        {errorMessage && <SignInAlert errorMessage={errorMessage}/>}
      </Box>
      <Input
        name="email"
        placeholder="Enter Username or Email"
        marginBottom="3"
        label={
          <Flex>
            <Typography variant="bodyBold" color="gray.800">
              Username or Email
            </Typography>
            <Spacer/>
            <Typography className="custom-link" color="teal.500">
              <ButtonLink
                testid="forgot-email-test"
                linkCaption="Forgot Username or Email?"
                handleClick={increment}
              />
            </Typography>
          </Flex>
        }
        tabIndex={1}
        type="text"
        data-testid="email-test"
        autoComplete="email"
        isRequired={true}
        value={email}
        onChange={handleEmailChange}
        error={emailError}
      />
      <PasswordInput
        name="password"
        placeholder="Enter your password"
        label={
          <Flex>
            <Typography variant="bodyBold" color="gray.800">
              Password
            </Typography>
            <Spacer/>
            <ButtonLink
              testid="forgot-password-test"
              linkCaption="Forgot Password?"
              handleClick={navigateToForgotPasswordAction.act}
            />
          </Flex>
        }
        tabIndex={2}
        data-testid="password-test"
        isRequired={true}
        value={password}
        onChange={handlePasswordChange}
        onKeyDown={onVerifyKeyDown}
        error={passwordError}
      />
      <Button
        isFullWidth
        data-testid="signin-button-test"
        style={{marginTop: "25px", marginBottom: "25px"}}
        onClick={signInAction.act}
      >
        Sign in
      </Button>
      <CancelButton />
      <Center>
        <Stack spacing="10px" direction="row">
          <Typography variant="bodyBold" color="gray.800">
            Don't have an account?
          </Typography>
            <ButtonLink
            testid="register-here-test"
            linkCaption="Register here"
            handleClick={navigateToCreateAccount}
            />
        </Stack>
      </Center>
    </Flex>
  );
}

const SignInAlert: FC<{ errorMessage: ReactNode }> = ({errorMessage}) => {
  const breakpoint = useBreakpoint({ssr: false});
  const isMobile = ["base", "sm"].includes(breakpoint);
  const descriptionStyleProps: StyleProps = {
    fontSize: `${isMobile ? "13" : "14"}px`,
  };
  const padding = `${isMobile ? "10" : "12"}px !important`;
  const alertStyleProps: StyleProps = {
    paddingTop: padding,
    paddingBottom: padding,
  };
  return (
    <ErrorAlert
      showCloseButton={false}
      descriptionStyleProps={descriptionStyleProps}
      alertStyleProps={alertStyleProps}
    >
      {errorMessage}
    </ErrorAlert>
  );
};
